import { getCurrentUser } from '@/lib/auth-utils'
import { fetchAccessibleCompanyIds } from '@/lib/company-access'
import { ROLE_ADMIN } from '@/lib/permissions'
import type { Booking } from '@/models'
import { auditLog } from '@/services/auditLog'
import { neon } from '@/services/neon'

export async function fetchBookings(companyId?: string): Promise<Booking[]> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  let query = neon
    .from('bookings')
    .select('*')
    .is('deleted_at', null)
    .order('check_in', { ascending: true })

  if (user.role === ROLE_ADMIN) {
    if (companyId) {
      query = query.eq('company_id', companyId)
    }
  } else {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (companyIds.length === 0) {
      return []
    }
    if (companyId) {
      if (!companyIds.includes(companyId)) {
        return []
      }
      query = query.eq('company_id', companyId)
    } else {
      query = query.in('company_id', companyIds)
    }
  }

  const { data, error } = await query
  if (error) {
    throw new Error(error.message)
  }
  return (data as Booking[]) || []
}

export async function fetchBookingById(id: string): Promise<Booking> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const { data, error } = await neon
    .from('bookings')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()
  if (error) {
    throw new Error(error.message)
  }

  const booking = data as Booking
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!booking.company_id || !companyIds.includes(booking.company_id)) {
      throw new Error('You do not have permission to view this booking.')
    }
  }

  return booking
}

export async function fetchBookingsByGuestId(guestId: string): Promise<Booking[]> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const { data, error } = await neon
    .from('bookings')
    .select('*')
    .eq('guest_id', guestId)
    .is('deleted_at', null)
    .order('check_in', { ascending: false })

  if (error) {
    throw new Error(error.message)
  }

  let bookings = (data as Booking[]) || []

  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    bookings = bookings.filter((b) => b.company_id && companyIds.includes(b.company_id))
  }

  return bookings
}

function validateBookingGuestsCount(value: unknown): void {
  if (value === undefined || value === null) return
  const n = Number(value)
  if (!Number.isInteger(n) || n < 1 || n > 100) {
    throw new Error('guests_count must be an integer between 1 and 100.')
  }
}

function validateBookingTime(value: unknown, field: string): void {
  if (value === undefined || value === null || value === '') return
  if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value)) {
    throw new Error(`${field} must be HH:MM`)
  }
  const [h, m] = value.split(':').map(Number)
  if (h < 0 || h > 23 || m < 0 || m > 59) throw new Error(`${field} must be HH:MM`)
}

export async function createBooking(payload: Partial<Booking>): Promise<Booking> {
  validateBookingGuestsCount((payload as any).guests_count)
  validateBookingTime((payload as any).check_in_time, 'check_in_time')
  validateBookingTime((payload as any).check_out_time, 'check_out_time')

  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  if (user.role !== ROLE_ADMIN) {
    if (!payload.company_id) {
      throw new Error('Company ID is required.')
    }
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!companyIds.includes(payload.company_id)) {
      throw new Error('You do not have permission to create bookings for this company.')
    }
  }

  const { data, error } = await neon
    .from('bookings')
    .insert({ ...payload, created_by: user.id })
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }
  const booking = data as Booking
  auditLog(user.id, 'bookings', booking.id, 'create', {
    guest_name: { to: booking.guest_name },
    check_in: { to: booking.check_in },
    check_out: { to: booking.check_out },
  })
  return booking
}

export async function updateBooking(id: string, payload: Partial<Booking>): Promise<Booking> {
  validateBookingGuestsCount((payload as any).guests_count)
  validateBookingTime((payload as any).check_in_time, 'check_in_time')
  validateBookingTime((payload as any).check_out_time, 'check_out_time')

  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const existing = await fetchBookingById(id)
  if (!existing) {
    throw new Error('Booking not found.')
  }
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!existing.company_id || !companyIds.includes(existing.company_id)) {
      throw new Error('You do not have permission to update this booking.')
    }
  }

  // TOCTOU fix: verify company_id + not soft-deleted at write time
  const { data, error } = await neon
    .from('bookings')
    .update(payload)
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) throw new Error('Booking not found or permission revoked — please refresh.')
    throw new Error(error.message)
  }
  const booking = data as Booking
  auditLog(user.id, 'bookings', id, 'update', { changes: { to: payload } })
  return booking
}

export async function deleteBooking(id: string): Promise<void> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const existing = await fetchBookingById(id)
  if (!existing) {
    throw new Error('Booking not found.')
  }
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!existing.company_id || !companyIds.includes(existing.company_id)) {
      throw new Error('You do not have permission to delete this booking.')
    }
  }

  const { error, data } = await (neon.from('bookings') as any)
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) return // already deleted / revoked — idempotent
    throw new Error(error.message)
  }
  if (!data) return
  auditLog(user.id, 'bookings', id, 'delete')
}
